import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { useImageUpload } from '../hooks/useImageUpload';
import type { EntityType, Media } from '@wineryxplorer/shared';

export function CloudinaryTest() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<EntityType>('venue');
  const [entityId, setEntityId] = useState('69350778bfeedae7d7fbd5c0'); // Real venue ID from seed data
  const [uploadedMedia, setUploadedMedia] = useState<Array<{
    publicId: string;
    url: string;
    timestamp: number;
  }>>([]);

  const { uploadImage, isUploading, progress, error } = useImageUpload();
  
  // Test signature generation
  const getSignature = trpc.media.getUploadSignature.useMutation();
  
  // Test media listing
  const { data: mediaList, refetch: refetchMedia } = trpc.media.listByEntity.useQuery(
    {
      entityType,
      entityId,
      limit: 10,
    },
    { enabled: false }
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const result = await uploadImage(selectedFile, {
        entityType,
        entityId,
        onSuccess: (publicId, url) => {
          setUploadedMedia((prev) => [
            { publicId, url, timestamp: Date.now() },
            ...prev,
          ]);
          setSelectedFile(null);
          setPreviewUrl(null);
        },
      });
      // eslint-disable-next-line no-console
      console.log('Upload successful:', result);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const handleTestSignature = async () => {
    try {
      const signature = await getSignature.mutateAsync({
        entityType,
        entityId,
      });
      // eslint-disable-next-line no-console
      console.log('Signature generated:', signature);
      alert('✅ Signature generated successfully! Check console for details.');
    } catch (err) {
      console.error('Signature generation failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (errorMessage.includes('logged in') || errorMessage.includes('Unauthorized')) {
        alert('❌ Authentication Error: You must be logged in to generate upload signatures.\n\nPlease authenticate first.');
      } else {
        alert(`❌ Signature generation failed: ${errorMessage}\n\nCheck console for details.`);
      }
    }
  };

  const handleListMedia = async () => {
    await refetchMedia();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl 2xl:max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Cloudinary Integration Test
        </h1>

        {/* Authentication Warning */}
        <div className="bg-amber-50 border border-amber-300 p-4 mb-6 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-900">
                <strong className="font-medium">Authentication Required:</strong> Upload features require you to be logged in.
                If you see 401 errors, you need to authenticate first.
              </p>
              <p className="text-sm text-amber-900 mt-2">
                <strong>To log in:</strong> This app uses Auth.js. You'll need to set up authentication providers
                or use the seed script to create test users. Check <code className="bg-amber-100 px-1 rounded">apps/api/.env</code> for auth configuration.
              </p>
            </div>
          </div>
        </div>

        {/* Configuration Section */}
        <div className="bg-gray-50 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Context Type
              </label>
              <select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value as EntityType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="venue">Venue</option>
                <option value="wine">Wine</option>
                <option value="event">Event</option>
                <option value="user">User</option>
                <option value="review">Review</option>
                <option value="shared">Shared</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Context ID
              </label>
              <input
                type="text"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="e.g., test-venue-123"
              />
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-gray-50 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Upload Image</h2>
          
          <div className="space-y-4">
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary-50 file:text-primary-700
                  hover:file:bg-primary-100"
              />
            </div>

            {previewUrl && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-w-xs rounded-lg border border-gray-300"
                />
                <p className="text-sm text-gray-500 mt-2">
                  File: {selectedFile?.name} ({(selectedFile?.size ?? 0 / 1024).toFixed(2)} KB)
                </p>
              </div>
            )}

            {isUploading && (
              <div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-primary-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2">Uploading... {progress}%</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-md
                hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed
                transition-colors"
            >
              {isUploading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>
        </div>

        {/* Test Actions Section */}
        <div className="bg-gray-50 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Test Actions</h2>
          <div className="space-y-3">
            <button
              onClick={handleTestSignature}
              disabled={getSignature.isPending}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-md
                hover:bg-primary-700 disabled:bg-gray-300 transition-colors"
            >
              {getSignature.isPending ? 'Testing...' : 'Test Signature Generation'}
            </button>
            <button
              onClick={handleListMedia}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-md
                hover:bg-green-700 transition-colors"
            >
              List Media for Context
            </button>
          </div>
        </div>

        {/* Uploaded Media Section */}
        {uploadedMedia.length > 0 && (
          <div className="bg-gray-50 rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Recently Uploaded</h2>
            <div className="grid grid-cols-2 gap-4">
              {uploadedMedia.map((media) => (
                <div key={media.timestamp} className="border rounded-lg p-3">
                  <img
                    src={media.url}
                    alt="Uploaded"
                    className="w-full h-48 object-cover rounded mb-2"
                  />
                  <p className="text-xs text-gray-600 break-all">
                    Public ID: {media.publicId}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(media.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Media List Section */}
        {mediaList && (
          <div className="bg-gray-50 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">
              Media from Database ({mediaList.total} total)
            </h2>
            {mediaList.items.length === 0 ? (
              <p className="text-gray-500">No media found for this context.</p>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {(mediaList.items as unknown as Media[]).map((media) => (
                  <div key={media.id} className="border rounded-lg p-3">
                    <img
                      src={media.cloudinaryUrl}
                      alt="Media"
                      className="w-full h-48 object-cover rounded mb-2"
                    />
                    <div className="text-xs space-y-1">
                      <p className="text-gray-600">
                        <span className="font-medium">Entity Type:</span> {media.entityType}
                      </p>
                      <p className="text-gray-600">
                        <span className="font-medium">Status:</span> {media.status}
                      </p>
                      <p className="text-gray-600 break-all">
                        <span className="font-medium">Public ID:</span> {media.cloudinaryPublicId}
                      </p>
                      {media.width && media.height && (
                        <p className="text-gray-600">
                          <span className="font-medium">Size:</span>{' '}
                          {media.width}x{media.height}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Instructions Section */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-primary-900 mb-3">
            How to Use This Test Page
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-primary-800 text-sm">
            <li>Select a context type and enter a context ID (or use defaults)</li>
            <li>Click "Test Signature Generation" to verify API connection</li>
            <li>Choose an image file to upload</li>
            <li>Click "Upload Image" to test the full upload flow</li>
            <li>Click "List Media for Context" to see all media in the database</li>
            <li>Check browser console for detailed logs</li>
          </ol>
          <div className="mt-4 pt-4 border-t border-primary-200">
            <p className="text-sm text-primary-700">
              <strong>Authentication Required:</strong> Upload features require authentication. 
              If you get 401 errors, you need to log in first.
            </p>
            <p className="text-sm text-primary-700 mt-2">
              See <code className="bg-primary-100 px-1 rounded">docs/guides/authentication-setup.md</code> for 
              instructions on setting up authentication for testing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
